import makeStyles from '@material-ui/core/styles/makeStyles';
import {createStyles} from '@material-ui/core/styles';
import {useSelector, useDispatch} from 'react-redux';
import {useState, useMemo, useCallback} from 'react';
import * as React from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import * as classNames from 'classnames';
import * as _ from 'lodash';
import InlineNotification from '../../../crazyEditor/components/EditorUI/BaseUI/InlineMessages/InlineNotification';
import {BILLING_PERIOD, BACKGROUND_REMOVAL, FOOTER_REMOVAL, INVENTORY_SYNC} from './Consts';
import styles from '../AccountPageStyles';
import {updateUserData} from '../../ViewDesigns/redux/modules/user';
import InfoDialog, {InfoDialogVariants} from 'cl-dialogs/InfoDialog';
import InlineButton from '../../../shared/components/InlineButton';
import {contactSupportWithMessage} from '../../../shared/helpers/analyticsHelper';
import LinearProgress from '@material-ui/core/LinearProgress';
import {editPaidFeatures} from '../../ViewDesigns/helpers/preferencesHelper';
import {StoreState} from '../../../App/initialStore';
import {BasicDialogProps} from 'cl-dialogs/BasicDialog';
import Img from '../../../shared/components/Img/Img';
import FormDialog from 'cl-dialogs/FormDialog';
import GeneralActions from '../../../shared/ActionsManager/GeneralActions/GeneralActions';
import {GENERAL_ACTIONS_TYPES} from '../../../shared/ActionsManager/ActionsTypes';
import {ACTION_DISPLAY_TYPES} from '../../../shared/ActionsManager/ActionsManagerConsts';
import {addDynamicDialog} from '../../../crazyEditor/redux/modules/panel/panel';

const useStyles = makeStyles(theme => {
  return createStyles({
    LinearProgress: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      backgroundColor: 'none',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: theme.spacing(50),
      width: '100%',
      position: 'relative',
      justifyContent: 'space-between',
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5),
    },
    button: {
      width: theme.spacing(28.75),
      position: 'relative',
    },
    linearProgress: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      backgroundColor: 'none',
    },
    img: {
      maxHeight: theme.spacing(16),
    },
  });
});

export interface EditPaidFeaturesDialogProps extends BasicDialogProps {
  open: boolean;
  paidFeatureName: string;
  onEnableFeature: () => any;
}

const EditPaidFeaturesDialog: React.FunctionComponent<EditPaidFeaturesDialogProps> = ({
  open,
  paidFeatureName,
  onClose,
  onEnableFeature,
  ...props
}) => {
  const billingPeriod = useSelector((state: StoreState) => state.user.billingPeriod);
  const footerRemoval = useSelector((state: StoreState) => state.user.isWhiteLabel);
  const backgroundRemoval = useSelector((state: StoreState) => state.user.backgroundRemoval);
  const inventorySync = useSelector((state: StoreState) => state.user.inventorySync);
  const paidFeaturesData = useSelector((state: StoreState) => state.user.paidFeatures);
  const dispatch = useDispatch();
  const [statusMessage, setStatusMessage] = useState(null);
  const active = useMemo(() => {
    switch (paidFeatureName) {
      case FOOTER_REMOVAL:
        return footerRemoval;
      case BACKGROUND_REMOVAL:
        return backgroundRemoval;
      case INVENTORY_SYNC:
        return inventorySync;
      default:
        return false;
    }
  }, [paidFeatureName, footerRemoval, backgroundRemoval, inventorySync]);
  const classes = useStyles();

  const getContactSupportErrorDialog = useCallback(() => {
    const contactSupport = () =>
      contactSupportWithMessage(
        'I want to add a paid feature but "It is not possible to add a paid feature at this point"'
      );
    return (
      <InfoDialog
        open={open}
        onClose={onClose}
        variant={InfoDialogVariants.Error}
        title="Adding Paid Feature Failed"
        actions={
          <GeneralActions
            types={[GENERAL_ACTIONS_TYPES.CONTACT_SUPPORT]}
            displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
            additionalActionData={{
              contactSupport,
            }}
          />
        }
        message={
          <div>
            It is not possible to add a paid feature at this point
            <br />
            Please{' '}
            <InlineButton
              onClick={() => {
                onClose();
                contactSupport();
              }}
            >
              contact support
            </InlineButton>{' '}
            for further assistance
          </div>
        }
      />
    );
  }, []);

  const paidFeatureData = useMemo(() => {
    const selectedPaidFeatureData = paidFeaturesData[paidFeatureName];
    const billingPeriodString = billingPeriod === BILLING_PERIOD.MONTH ? 'month' : 'year';
    let chargeAmount, priceText, type, img, firstText, secondText;
    if (selectedPaidFeatureData) {
      ({chargeAmount, type} = selectedPaidFeatureData);
      priceText = `US $${selectedPaidFeatureData['price']} / ${billingPeriodString}`;
      img = require('./images/' + selectedPaidFeatureData['imgRelativeUrl']);
      firstText = selectedPaidFeatureData['headerText'];
      secondText = selectedPaidFeatureData['headlineText'];
    }
    const displayName = _.keys(paidFeaturesData)[type - 1];
    const chargeMessage = (
      <span>
        You'll be immediately charged an additional <strong>US ${chargeAmount}</strong>
      </span>
    );
    return {
      chargeAmount,
      priceText,
      chargeMessage,
      img,
      firstText,
      secondText,
      type,
      displayName,
      ..._.pick(selectedPaidFeatureData, ['activateButtonText', 'deactivationConsequence', 'eventPropertyName']),
    };
  }, [paidFeaturesData, paidFeatureName]);

  const handlePaidFeatureOk = useCallback(() => {
    const showErrorDialog = (error = 'Unknown Error') => {
      onClose();
      const contactSupport = () => {
        contactSupportWithMessage(`I got this error message while trying to resolve a payment issue: ${error}`);
        onClose();
      };
      dispatch(
        addDynamicDialog(InfoDialog, {
          open,
          onClose,
          title: 'Adding Paid Feature Failed',
          variant: InfoDialogVariants.Error,
          instructionText: {
            before: (
              <div>
                We ran into some trouble processing your payment. <br />
                This is the error we get: <br />
              </div>
            ),
            after: (
              <div>
                We encourage you to contact PayPal for more information.
                <br />
                Let’s get you back on track, once ready:{' '}
                <InlineButton onClick={handlePaidFeatureOk}>retry payment.</InlineButton>
                <br />
                Still have questions? <InlineButton onClick={contactSupport}>contact support.</InlineButton>
              </div>
            ),
          },
          message: <div>{error}</div>,
          actions: (
            <React.Fragment>
              <GeneralActions
                types={[GENERAL_ACTIONS_TYPES.CONTACT_SUPPORT]}
                displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
                additionalActionData={{
                  contactSupport,
                }}
              />
              <GeneralActions
                types={[GENERAL_ACTIONS_TYPES.RETRY_PAYMENT]}
                displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
                additionalActionData={{
                  handlePaidFeatureOk,
                }}
              />
            </React.Fragment>
          ),
        })
      );
    };
    const {type, eventPropertyName} = paidFeatureData;
    editPaidFeatures(eventPropertyName, type, !active)
      .then(response => {
        if (response.success) {
          if (onEnableFeature) {
            onEnableFeature();
          }
          dispatch(updateUserData(response.user));
          onClose();
          setStatusMessage(null);
        } else {
          showErrorDialog(response.message);
        }
      })
      .catch(response => {
        showErrorDialog(response.responseJSON.message);
      });
    setStatusMessage(!active ? 'processing payment' : 'deactivating feature');
  }, [active, onEnableFeature]);

  const {chargeAmount, priceText, chargeMessage, img, firstText, secondText, activateButtonText} = paidFeatureData;

  if (!active) {
    return chargeAmount < 0 && open ? (
      getContactSupportErrorDialog()
    ) : (
      <FormDialog
        open={open}
        onClose={onClose}
        formName={_.uniqueId('Dialog_')}
        content={
          <div id={`cla-paid-feature-dialog`} className={classes.content}>
            <div>{firstText}</div>
            <div style={styles.h3b}>{secondText}</div>
            <div>{priceText}</div>
            <Img className={classNames('cla-img', classes.img)} src={img} />
            <InlineNotification animate={false}>{chargeMessage}</InlineNotification>
            <RaisedButton
              disabled={!!statusMessage}
              primary
              className={classNames('cla-btn-enable', classes.button)}
              label={statusMessage || activateButtonText}
              onClick={handlePaidFeatureOk}
            >
              {statusMessage && <LinearProgress variant="indeterminate" className={classes.linearProgress} />}
            </RaisedButton>
          </div>
        }
        {...props}
      />
    );
  }
  const {displayName, deactivationConsequence} = paidFeatureData;
  const message = (
    <span>
      You are about to turn off {paidFeatureName}
      {paidFeatureName === INVENTORY_SYNC ? ' for all of your eBay and Amazon accounts' : undefined}.
      <br />
      {deactivationConsequence ? (
        <span>
          {deactivationConsequence} <br />
        </span>
      ) : (
        undefined
      )}
      You will not be billed for this feature anymore.
    </span>
  );
  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      variant={InfoDialogVariants.Error}
      title={'Are you sure you want to turn off ' + displayName + '?'}
      actions={
        <GeneralActions
          types={[GENERAL_ACTIONS_TYPES.TURN_PAID_FEATURE_OFF]}
          displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
          additionalActionData={{handlePaidFeatureOk}}
        />
      }
      message={message}
    />
  );
};

export default EditPaidFeaturesDialog;
